import { useState, useEffect } from "react";
import { X, Plus, Trash2, Loader2, Filter, Pencil, Check, ArrowUpDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface NewsFilter {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
  is_active: boolean;
}

interface NewsFilterManagerProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewsFilterManager = ({ isOpen, onClose }: NewsFilterManagerProps) => {
  const [filters, setFilters] = useState<NewsFilter[]>([]);
  const [loading, setLoading] = useState(true);
  const [newFilterName, setNewFilterName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [deleteFilter, setDeleteFilter] = useState<NewsFilter | null>(null);
  const [editingFilter, setEditingFilter] = useState<NewsFilter | null>(null);
  const [editName, setEditName] = useState("");
  const [movingFilter, setMovingFilter] = useState<NewsFilter | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<string>("");

  useEffect(() => {
    if (isOpen) {
      fetchFilters();
    }
  }, [isOpen]);

  const fetchFilters = async () => {
    try {
      const { data, error } = await supabase
        .from("news_filters")
        .select("*")
        .order("sort_order", { ascending: true });

      if (error) throw error;
      setFilters(data || []);
    } catch (error) {
      console.error("Error fetching news filters:", error);
      toast.error("Errore nel caricamento dei filtri");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (text: string) => {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .trim();
  };

  const handleAddFilter = async () => {
    if (!newFilterName.trim()) {
      toast.error("Inserisci un nome per il filtro");
      return;
    }

    setIsSaving(true);
    try {
      const slug = generateSlug(newFilterName);
      const maxOrder = Math.max(...filters.map(f => f.sort_order || 0), 0);
      
      const { error } = await supabase.from("news_filters").insert({
        name: newFilterName.trim(),
        slug,
        sort_order: maxOrder + 1,
      });

      if (error) throw error;

      toast.success("Filtro creato!");
      setNewFilterName("");
      fetchFilters();
    } catch (error) {
      console.error("Error creating filter:", error);
      toast.error("Errore nella creazione del filtro");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteFilter = async () => {
    if (!deleteFilter) return;

    // Don't allow deleting the "all" filter
    if (deleteFilter.slug === "all") {
      toast.error("Non puoi eliminare il filtro 'Tutte'");
      setDeleteFilter(null);
      return;
    }

    try {
      const { error } = await supabase
        .from("news_filters")
        .delete()
        .eq("id", deleteFilter.id);

      if (error) throw error;

      toast.success("Filtro eliminato!");
      fetchFilters();
    } catch (error) {
      console.error("Error deleting filter:", error);
      toast.error("Errore nell'eliminazione del filtro");
    } finally {
      setDeleteFilter(null);
    }
  };

  const handleStartEdit = (filter: NewsFilter) => {
    if (filter.slug === "all") {
      toast.error("Non puoi modificare il filtro 'Tutte'");
      return;
    }
    setEditingFilter(filter);
    setEditName(filter.name);
  };

  const handleSaveEdit = async () => {
    if (!editingFilter || !editName.trim()) return;

    try {
      const { error } = await supabase
        .from("news_filters")
        .update({
          name: editName.trim(),
          slug: generateSlug(editName),
        })
        .eq("id", editingFilter.id);

      if (error) throw error;

      toast.success("Filtro aggiornato!");
      setEditingFilter(null);
      setEditName("");
      fetchFilters();
    } catch (error) {
      console.error("Error updating filter:", error);
      toast.error("Errore nell'aggiornamento del filtro");
    }
  };

  const handleStartMove = (filter: NewsFilter) => {
    setMovingFilter(filter);
    setSelectedPosition("");
  };

  const handleConfirmMove = async () => {
    if (!movingFilter || !selectedPosition) return;

    const sortedFilters = [...filters].sort((a, b) => a.sort_order - b.sort_order);
    const targetIndex = parseInt(selectedPosition);
    const currentIndex = sortedFilters.findIndex(f => f.id === movingFilter.id);

    if (targetIndex === currentIndex) {
      setMovingFilter(null);
      return;
    }

    try {
      const newOrder = [...sortedFilters];
      const [moved] = newOrder.splice(currentIndex, 1);
      newOrder.splice(targetIndex, 0, moved);

      for (let i = 0; i < newOrder.length; i++) {
        await supabase
          .from("news_filters")
          .update({ sort_order: i })
          .eq("id", newOrder[i].id);
      }

      toast.success("Posizione aggiornata!");
      fetchFilters();
    } catch (error) {
      console.error("Error moving filter:", error);
      toast.error("Errore nello spostamento del filtro");
    } finally {
      setMovingFilter(null);
    }
  };

  const getPositionOptions = () => {
    const sortedFilters = [...filters].sort((a, b) => a.sort_order - b.sort_order);
    return sortedFilters.map((f, index) => ({
      value: index.toString(),
      label: `${index + 1}. ${f.name}`,
      isCurrent: movingFilter ? f.id === movingFilter.id : false
    }));
  };

  const renderFilterItem = (filter: NewsFilter) => {
    const isEditing = editingFilter?.id === filter.id;
    const isProtected = filter.slug === "all";

    return (
      <motion.div
        key={filter.id}
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="flex items-center justify-between p-3 rounded-xl hover:bg-muted/50 transition-colors group"
      >
        <div className="flex items-center gap-2 flex-1">
          <Filter className="w-4 h-4 text-primary" />
          
          {isEditing ? (
            <div className="flex items-center gap-2 flex-1">
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="h-8 text-sm"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSaveEdit();
                  if (e.key === "Escape") setEditingFilter(null);
                }}
              />
              <button
                onClick={handleSaveEdit}
                className="p-1.5 rounded-full text-green-500 hover:bg-green-500/10 transition-all"
              >
                <Check className="w-4 h-4" />
              </button>
              <button
                onClick={() => setEditingFilter(null)}
                className="p-1.5 rounded-full text-muted-foreground hover:bg-muted transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <span className="font-medium">{filter.name}</span>
          )}
        </div>
        
        {!isEditing && !isProtected && (
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button
              onClick={() => handleStartMove(filter)}
              className="p-1.5 rounded-full text-blue-500 hover:bg-blue-500/10 transition-all"
              title="Sposta"
            >
              <ArrowUpDown className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleStartEdit(filter)}
              className="p-1.5 rounded-full text-primary hover:bg-primary/10 transition-all"
              title="Modifica"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDeleteFilter(filter)}
              className="p-1.5 rounded-full text-destructive hover:bg-destructive/10 transition-all"
              title="Elimina"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </motion.div>
    );
  };

  return (
    <>
      <Drawer open={isOpen} onOpenChange={onClose} shouldScaleBackground={false}>
        <DrawerContent className="h-[85vh] bg-background border-t border-border">
          <DrawerHeader className="relative border-b border-border">
            <button
              onClick={onClose}
              className="absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full hover:bg-muted transition-all"
            >
              <X className="w-5 h-5" />
            </button>
            <DrawerTitle className="text-xl font-bold text-center">
              Gestione Filtri News
            </DrawerTitle>
          </DrawerHeader>

          <div className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Add New Filter */}
            <div className="space-y-3 p-4 bg-muted/30 rounded-2xl">
              <h3 className="text-sm font-medium text-foreground">
                Nuovo Filtro
              </h3>
              <Input
                value={newFilterName}
                onChange={(e) => setNewFilterName(e.target.value)}
                placeholder="Nome filtro (es: Più recenti)"
              />
              <Button
                onClick={handleAddFilter}
                disabled={isSaving}
                className="w-full gap-2"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Aggiungi Filtro
              </Button>
            </div>

            {/* Filters List */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-foreground px-1">
                Filtri esistenti
              </h3>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-primary" />
                </div>
              ) : filters.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground border border-dashed rounded-xl">
                  <Filter className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nessun filtro creato</p>
                </div>
              ) : (
                <AnimatePresence>
                  {[...filters].sort((a, b) => a.sort_order - b.sort_order).map(renderFilterItem)}
                </AnimatePresence>
              )}
            </div>
          </div>
        </DrawerContent>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteFilter} onOpenChange={() => setDeleteFilter(null)}>
        <AlertDialogContent className="bg-background border border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo filtro?</AlertDialogTitle>
            <AlertDialogDescription>
              Stai per eliminare "{deleteFilter?.name}". Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteFilter}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move Position Dialog */}
      <AlertDialog open={!!movingFilter} onOpenChange={() => setMovingFilter(null)}>
        <AlertDialogContent className="bg-background border border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Sposta "{movingFilter?.name}"</AlertDialogTitle>
            <AlertDialogDescription>
              Seleziona la nuova posizione per questo filtro.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Select value={selectedPosition} onValueChange={setSelectedPosition}>
              <SelectTrigger className="bg-background">
                <SelectValue placeholder="Seleziona posizione" />
              </SelectTrigger>
              <SelectContent className="bg-background border border-border">
                {getPositionOptions().map((option) => (
                  <SelectItem 
                    key={option.value} 
                    value={option.value}
                    className={option.isCurrent ? "font-bold text-primary" : ""}
                  >
                    {option.label} {option.isCurrent && "(attuale)"}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmMove}
              disabled={!selectedPosition}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              Sposta
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default NewsFilterManager;
